import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import moment from "moment";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import InvestorStatusBadge from "../../../components/Investor/InvestorStatusBadge";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { CardComp, ButtonComp } from "../../../components";
import { formatMoney } from "../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../utils/helpers";
import { investorButtonProps } from "../../../components/Investor/investorButtonStyles";
import {
  useGetMarketplaceOrderQuery,
  useCancelMarketplaceOrderMutation,
} from "../../../store/investor/investorMarketplaceApi";
import {
  InvestorApiErrorState,
  InvestorEmptyState,
} from "../../../components/Investor/InvestorPlatformState";
import UnifiedTimeline from "../../../components/shared/Timeline/UnifiedTimeline";

export default function InvestorMarketplaceOrderDetailPage() {
  const { orderNumber } = useParams();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const { data: order, isFetching, isError, refetch } = useGetMarketplaceOrderQuery(orderNumber, {
    skip: !orderNumber,
  });
  const [cancelOrder, { isLoading: isCancelling }] = useCancelMarketplaceOrderMutation();

  const handleCancel = async () => {
    try {
      await cancelOrder({ orderNumber }).unwrap();
      toast.success("Order cancelled");
      setConfirmCancel(false);
    } catch (error) {
      toast.error(error?.data?.message || "Unable to cancel this order.");
    }
  };

  if (isFetching) {
    return (
      <InvestorPageShell
        title="Order"
        backTo="/invest/requests?filter=marketplace"
        backLabel="Back to requests"
      >
        <TableSkeletonLoader rows={6} columns={2} />
      </InvestorPageShell>
    );
  }

  if (isError || !order) {
    return (
      <InvestorPageShell
        title="Order"
        backTo="/invest/requests?filter=marketplace"
        backLabel="Back to requests"
      >
        {isError ? (
          <InvestorApiErrorState
            message="We couldn’t load this order. Please try again."
            onRetry={() => refetch()}
          />
        ) : (
          <InvestorEmptyState
            title="Order not found"
            message="This order may have been removed or the link is incorrect."
          />
        )}
      </InvestorPageShell>
    );
  }

  const timeline = order.timeline || [];

  return (
    <InvestorPageShell
      title={`Order ${order.orderNumber}`}
      subtitle={order.title}
      backTo="/invest/requests?filter=marketplace"
      backLabel="Back to requests"
    >
      <div className="row g-3">
        <div className="col-lg-8">
          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <InvestorStatusBadge status={order.status} />
              <span className="text-muted small">{order.paymentStatusLabel}</span>
            </div>

            {order.nextStep ? (
              <div
                className="alert py-2 px-3 small mb-3"
                style={{
                  background: "rgba(49, 119, 140, 0.08)",
                  border: "1px solid rgba(49, 119, 140, 0.25)",
                  color: "#31778c",
                }}
              >
                {order.nextStep}
              </div>
            ) : null}

            <div className="row g-3 small">
              <div className="col-sm-6">
                <p className="text-muted mb-1">Submitted</p>
                <p className="fw-semibold mb-0">
                  {order.submittedAt ? moment(order.submittedAt).format("DD-MMM-YYYY HH:mm") : "—"}
                </p>
              </div>
              <div className="col-sm-6">
                <p className="text-muted mb-1">Delivery method</p>
                <p className="fw-semibold mb-0">
                  {convertToTitleCase((order.deliveryMethod || "").replace(/_/g, " ")) || "—"}
                </p>
              </div>
              <div className="col-sm-6">
                <p className="text-muted mb-1">Checkout mode</p>
                <p className="fw-semibold mb-0">
                  {convertToTitleCase((order.checkoutMode || "").replace(/_/g, " ")) || "—"}
                </p>
              </div>
              {order.paymentReference ? (
                <div className="col-sm-6">
                  <p className="text-muted mb-1">Payment reference</p>
                  <p className="fw-semibold mb-0 text-break">{order.paymentReference}</p>
                </div>
              ) : null}
              {order.paymentConfirmedAt ? (
                <div className="col-sm-6">
                  <p className="text-muted mb-1">Payment confirmed</p>
                  <p className="fw-semibold mb-0">
                    {moment(order.paymentConfirmedAt).format("DD-MMM-YYYY HH:mm")}
                  </p>
                </div>
              ) : null}
              {order.stockHoldExpiresAt && order.paymentStatus === "pending" ? (
                <div className="col-sm-6">
                  <p className="text-muted mb-1">Payment hold expires</p>
                  <p className="fw-semibold mb-0">
                    {moment(order.stockHoldExpiresAt).format("DD-MMM-YYYY HH:mm")}
                  </p>
                </div>
              ) : null}
              <div className="col-sm-6">
                <p className="text-muted mb-1">Contact</p>
                <p className="fw-semibold mb-0">{order.fullName || "—"}</p>
                {order.phone ? <p className="text-muted mb-0">{order.phone}</p> : null}
                {order.email ? <p className="text-muted mb-0">{order.email}</p> : null}
              </div>
              {order.deliveryAddress ? (
                <div className="col-12">
                  <p className="text-muted mb-1">Delivery address</p>
                  <p className="mb-0">
                    {[
                      order.deliveryAddress.line1,
                      order.deliveryAddress.line2,
                      order.deliveryAddress.city,
                      order.deliveryAddress.state,
                      order.deliveryAddress.postal_code,
                    ]
                      .filter(Boolean)
                      .join(", ") || String(order.deliveryAddress)}
                  </p>
                </div>
              ) : null}
              {order.pickupLocation ? (
                <div className="col-12">
                  <p className="text-muted mb-1">Pickup location</p>
                  <p className="mb-0">{order.pickupLocation}</p>
                </div>
              ) : null}
              {order.customerNote ? (
                <div className="col-12">
                  <p className="text-muted mb-1">Your note</p>
                  <p className="mb-0">{order.customerNote}</p>
                </div>
              ) : null}
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <h6 className="fw-semibold mb-3">Items</h6>
            {(order.items || []).length === 0 ? (
              <p className="text-muted small mb-0">No line items.</p>
            ) : (
              <>
                <div
                  className="d-none d-sm-grid small fw-semibold text-muted px-2 pb-2 mb-1 border-bottom"
                  style={{
                    gridTemplateColumns: "minmax(0, 1fr) 56px 96px 104px",
                    gap: "0.75rem",
                  }}
                >
                  <span>Product</span>
                  <span className="text-center">Qty</span>
                  <span className="text-end">Unit</span>
                  <span className="text-end">Total</span>
                </div>
                <div className="d-flex flex-column">
                  {order.items.map((item) => (
                    <div
                      key={item.id || `${item.productId}-${item.sku}`}
                      className="px-2 py-3 border-bottom"
                    >
                      <div
                        className="d-grid align-items-start"
                        style={{
                          gridTemplateColumns: "minmax(0, 1fr) 56px 96px 104px",
                          gap: "0.75rem",
                        }}
                      >
                        <div className="min-w-0">
                          <p className="fw-semibold mb-0 text-truncate" style={{ color: "#383838" }}>
                            {item.productName}
                          </p>
                          {item.unitLabel || item.sku ? (
                            <p className="text-muted small mb-0 text-truncate">
                              {[item.unitLabel, item.sku ? `SKU ${item.sku}` : null]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          ) : null}
                          <div className="d-sm-none small text-muted mt-2">
                            Qty {item.quantity} · {formatMoney(item.unitPrice)} each
                          </div>
                        </div>
                        <div className="d-none d-sm-block text-center fw-semibold">{item.quantity}</div>
                        <div className="d-none d-sm-block text-end">{formatMoney(item.unitPrice)}</div>
                        <div className="text-end fw-semibold" style={{ color: "#383838" }}>
                          {formatMoney(item.lineTotal)}
                          <span className="d-sm-none text-muted small d-block fw-normal">Line total</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="pt-3 mt-1 small">
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Subtotal</span>
                <span>{formatMoney(order.subtotal)}</span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Delivery fee</span>
                <span>{formatMoney(order.deliveryFee)}</span>
              </div>
              {order.checkoutMode === "pay_online" ? (
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Payment charge</span>
                  <span>{formatMoney(order.paymentFee || 0)}</span>
                </div>
              ) : null}
              <div className="d-flex justify-content-between fw-bold pt-2 border-top">
                <span>
                  {order.checkoutMode === "pay_online" && order.paymentStatus === "confirmed"
                    ? "Total paid"
                    : "Total"}
                </span>
                <span style={{ color: "var(--color-primary)" }}>
                  {formatMoney(
                    order.checkoutMode === "pay_online" && order.paymentTotal
                      ? order.paymentTotal
                      : order.totalAmount
                  )}
                </span>
              </div>
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <h6 className="fw-semibold mb-3">Timeline</h6>
            <UnifiedTimeline
              items={timeline}
              showActor={false}
              emptyMessage="No updates yet."
            />
          </CardComp>
        </div>

        <div className="col-lg-4">
          <CardComp cardClassName="border-0 shadow-sm rounded-4">
            <h6 className="fw-semibold mb-3">Actions</h6>
            {order.canCancel ? (
              <ButtonComp
                btnText="Cancel order"
                onClick={() => setConfirmCancel(true)}
                loading={isCancelling}
                btnClassName="w-100 mb-2"
                {...investorButtonProps.outline}
                ButtonInlineStyle={{
                  ...investorButtonProps.outline.ButtonInlineStyle,
                  width: "100%",
                  borderColor: "#FF4343",
                  color: "#FF4343",
                }}
              />
            ) : (
              <p className="text-muted small mb-2">This order can no longer be cancelled online.</p>
            )}
            <Link to="/invest/marketplace" className="d-block">
              <ButtonComp
                btnText="Browse marketplace"
                btnClassName="w-100"
                {...investorButtonProps.primary}
                ButtonInlineStyle={{ ...investorButtonProps.primary.ButtonInlineStyle, width: "100%" }}
              />
            </Link>
          </CardComp>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleCancel}
        title="Cancel order"
        message={`Cancel order ${order.orderNumber}? This cannot be undone.`}
        confirmText="Yes, cancel"
        confirmButtonClass="bg-danger text-white"
        isLoading={isCancelling}
      />
    </InvestorPageShell>
  );
}
